import React from 'react';
import { Box, Button, Divider, Popover, Typography } from '@mui/material';
import { Theme, useTheme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks';
import { isCloudSite } from '../../electron-modules';
import { AccessType } from '../../types/project';

interface AccountPopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: {
      margin: theme.spacing(0, 1.5)
    },
    buttonParent: {
      display: "flex",
      padding: theme.spacing(0.5),
      justifyContent: "center"
    }
  })
);

export const AccountPopover = (props: AccountPopoverProps) => {
  const { anchorEl, onClose, open = false } = props;

  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const {linkAccount, signOut, loggedIn, user, permissions} = useAuth();

  const getAccountType = () => {
    switch (permissions.type) {
    case AccessType.ViewOnly:
      return t('user-account.view_only');
    case AccessType.Standard:
      return t('user-account.standard_user');
    case AccessType.Admin:
      return t('user-account.administrator');
    default:
      return t('user-account.unknown_user_type');
    }
  };

  const accountType = getAccountType();

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'bottom'
      }}
      disableScrollLock
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top'
      }}
      onClose={onClose}
      open={open}
      PaperProps={{
        style: {
          width: 200
        }
      }}
    >
      {/** Standard user profile */}
      {loggedIn && <React.Fragment>
        <Box style={{
          padding: theme.spacing(2),
          wordBreak: "break-all"
        }}>
          {(user?.firstName || user?.lastName) && <Typography
            variant="body1"
            style={{
              textTransform: "capitalize"
            }}
          >
            {`${user.firstName} ${user.lastName}`}
          </Typography>}

          {isCloudSite && <Typography
            color="textSecondary"
            variant="body2"
            style={{
              textTransform: "capitalize"
            }}
          >
            {`(${accountType})`}
          </Typography>}

          {user?.email && <Typography
            color="textSecondary"
            variant="body2"
            style={{
              paddingTop: theme.spacing(1)
            }}
          >
            {user.email}
          </Typography>}

        </Box>

        <Divider className={classes.divider}/>

        <Box className={classes.buttonParent}>
          <Button
            onClick={signOut}
            size="small"
            style={{
              textTransform: "capitalize"
            }}
          >
            {t("user-account.logout_engine_cloud")}
          </Button>
        </Box>
      </React.Fragment>}

      {/** Anonymous view only */}
      {!loggedIn && isCloudSite && <React.Fragment>
        <Box style={{
          padding: theme.spacing(2)
        }}>
          <Typography variant="subtitle2">
            {t('user-account.accessing_the_project_as_an_anonymous')}
          </Typography>
        </Box>

        <Divider className={classes.divider}/>

        <Box className={classes.buttonParent}>
          <Button
            onClick={signOut}
            size="small"
            style={{
              textTransform: "capitalize"
            }}
          >
            {t("user-account.login_engine_cloud")}
          </Button>
        </Box>
      </React.Fragment>}

      {/** Unlinked account */}
      {!loggedIn && !isCloudSite && <React.Fragment>
        <Box style={{
          padding: theme.spacing(2)
        }}>
          <Typography variant="subtitle2">
            {t('user-account.an_linked_engine_cloud_account_is_required')}
          </Typography>
        </Box>

        <Divider className={classes.divider}/>

        <Box className={classes.buttonParent}>
          <Button
            onClick={linkAccount}
            size="small"
            style={{
              textTransform: "capitalize"
            }}
          >
            {t("user-account.login_engine_cloud")}
          </Button>
        </Box>
      </React.Fragment>}

    </Popover>
  );
};
